import { createContext, useContext, useEffect, useState } from 'react'
import { watchAuth, signInWithGoogle, signOut, WORKSPACE_DOMAIN } from './firebase.js'
import { upsertUserProfile, reconcilePendingUser, updateUserProfile } from './data.js'
import { initialsOf, paletteFor } from './format.js'
import { connectGoogle, getGoogleState, refreshDirectoryProfile } from './google.js'

const AuthContext = createContext(null)

export function AuthProvider({ children, initialUser = null }) {
  const [user, setUser] = useState(initialUser)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    return watchAuth(async (fbUser) => {
      if (fbUser) {
        // If this person was pre-invited (a pending roster entry by email),
        // link it to their real account and re-point their tasks/projects.
        let merged = null
        try {
          merged = await reconcilePendingUser(fbUser)
        } catch (e) {
          /* non-fatal — fall back to a fresh profile */
        }
        const name = merged?.name || fbUser.displayName || fbUser.email
        const palette = merged && merged.color ? { color: merged.color, ink: merged.ink } : paletteFor(fbUser.uid)
        const profile = {
          uid: fbUser.uid,
          name,
          email: fbUser.email,
          initials: merged?.initials || initialsOf(name),
          photoURL: fbUser.photoURL || null,
          ...palette,
          startupBehavior: (merged && merged.startupBehavior) === 'home' ? 'home' : 'continue',
        }
        // Best-effort profile mirror; don't block sign-in if it fails offline.
        try {
          await upsertUserProfile(fbUser, {
            name: profile.name,
            initials: profile.initials,
            color: profile.color,
            ink: profile.ink,
            pending: false,
          })
        } catch (e) {
          /* surfaced elsewhere; not fatal to the session */
        }
        // Consent for the API scopes is granted at login (firebase.js addScope), so
        // acquire the working GIS token silently now — no separate "Connect" step.
        // Fire-and-forget: any failure (revoked grant, or the OAuth client not yet
        // aligned) must never block sign-in; Settings then offers Reconnect.
        if (typeof window !== 'undefined' && getGoogleState().status !== 'connected') {
          connectGoogle({ hint: fbUser.email })
            .then(() => refreshDirectoryProfile((patch) => updateUserProfile(fbUser.uid, patch), { hint: fbUser.email }))
            .then((patch) => { if (patch && Object.keys(patch).length) setUser((prev) => (prev ? { ...prev, ...patch } : prev)) })
            .catch(() => { /* silent grant unavailable — Settings offers Reconnect */ })
        }
        setUser(profile)
      } else {
        setUser(null)
      }
      setLoading(false)
    })
  }, [])

  async function signIn() {
    setError(null)
    try {
      await signInWithGoogle()
    } catch (e) {
      setError(e.message || 'Sign-in failed')
    }
  }

  // Update the signed-in user's own profile (name / avatar color); writes to
  // Firestore and reflects immediately in the UI.
  async function updateProfile(patch) {
    if (!user) return
    setUser((prev) => ({ ...prev, ...patch }))
    await updateUserProfile(user.uid, patch)
  }

  const value = { user, loading, error, signIn, signOut, updateProfile, workspaceDomain: WORKSPACE_DOMAIN }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
